import { isEmpty } from 'lodash';
import React, { useState, useEffect, forwardRef } from 'react';
import DatePicker, { CalendarContainer } from 'react-datepicker';
import moment from 'moment';

const ExampleCustomInput = forwardRef(function ExampleCustomInput({ fontSize, value, onClick, placeholder, disabled, style }, ref) {
  const styleInput = fontSize?.value ? { fontSize: `${fontSize?.value}px` } : {};

  if (styleInput) {
    style = { ...style, ...styleInput };
  }
  return (
    <>
      <input
        type="text"
        value={value}
        style={style}
        disabled={disabled}
        className={`${isEmpty(styleInput) && 'f-16'} pr-4 cursor-pointer`}
        autoComplete="off"
        placeholder={placeholder}
        readOnly
        onClick={onClick}
      />
      <button aria-label="Calendar" type="button" className="btn-calendar font-initial" style={{ top: 8 }} onClick={onClick} ref={ref} disabled={disabled}>
        <span className="sr-only">Calendar</span>
        <i className="icon-Calender position-relative f-18" />
      </button>
    </>
  );
});
ExampleCustomInput.displayName = 'ExampleCustomInput';

function CustomDatePicker({
  dateFormat,
  valueProp,
  name,
  placeholder,
  onChange,
  disable,
  innerRef,
  popperPlace,
  popperProps,
  readOnly,
  disableCalendar,
  keyProp,
  portalId,
  filterDate,
  classes,
  showTime,
  isRange,
  startDate,
  endDate,
  minDate,
  style,
  onCalendarOpen,
  onCalendarClose, // Add onCalendarOpen and onCalendarClose props
  fontSize,
  isDateClearable,
}) {
  const [singleDate, setSingleDate] = useState(valueProp ? new Date(valueProp) : null); // State for single date
  const [showMonth, setShowMonth] = useState(false);

  useEffect(() => {
    setSingleDate(valueProp ? new Date(valueProp) : null);
  }, [valueProp]);

  const [showYear, setShowYear] = useState(false);
  const [showDateTime, setShowDateTime] = useState(false);

  const handleChange = (date) => {
    if (!isRange) {
      if (singleDate?.getTime() === date?.getTime() || (!singleDate && !date)) return;
      setSingleDate(date);
    } else {
      date = date?.map((item) => (item ? moment(new Date(item)).format('DD-MMM-YYYY') : null));
    }
    onChange(date);
  };

  function MyContainer({ className, children }) {
    return (
      <CalendarContainer className={className}>
        <div style={{ position: 'relative' }}>{children}</div>
        {showTime && (
          <div className="custom-Time-picker">
            <div className="custom-control custom-switch position-absolute" style={{ left: '20px', paddingTop: '5px' }}>
              <input
                type="checkbox"
                className="custom-control-input"
                id="customSwitch1-date-1256"
                onClick={() => setShowDateTime(!showDateTime)}
                checked={showDateTime}
              />
              <label className="custom-control-label" htmlFor="customSwitch1-date-1256" />
            </div>
          </div>
        )}
      </CalendarContainer>
    );
  }

  const monthSelection = () => {
    setShowMonth(false);
  };

  const yearSelection = () => {
    setShowYear(false);
  };

  const handleOustSide = () => {
    setShowMonth(false);
    setShowYear(false);
  };

  const monthandDateChange = () => {
    return showMonth ? monthSelection : handleChange;
  };

  const monthFormate = () => {
    return showMonth ? 'MMMM yyyy' : dateFormat;
  };

  const customHeaders = () => {
    return showMonth
      ? ({ monthDate, decreaseYear, increaseYear }) => (
          <div>
            <button
              aria-label="Previous Year"
              className="react-datepicker__navigation react-datepicker__navigation--previous"
              type="button"
              onClick={decreaseYear}
            >
              <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">{'<'}</span>
            </button>
            <span className="react-datepicker__current-month d-flex align-item-center justify-content-center">
              <div onClick={() => setShowYear(true)} className="px-1">
                {monthDate.toLocaleString('en-US', {
                  year: 'numeric',
                })}
              </div>
            </span>
            <button aria-label="Next Year" className="react-datepicker__navigation react-datepicker__navigation--next" onClick={increaseYear} type="button">
              <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">{'>'}</span>
            </button>
          </div>
        )
      : ({ monthDate, decreaseMonth, increaseMonth }) => (
          <div>
            <button
              aria-label="Previous Month"
              className="react-datepicker__navigation react-datepicker__navigation--previous"
              type="button"
              onClick={decreaseMonth}
            >
              <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">{'<'}</span>
            </button>
            <span className="react-datepicker__current-month d-flex align-item-center justify-content-center">
              <div onClick={() => setShowMonth(true)}>
                {monthDate.toLocaleString('en-US', {
                  month: 'long',
                })}
              </div>
              <div onClick={() => setShowYear(true)} className="px-1">
                {monthDate.toLocaleString('en-US', {
                  year: 'numeric',
                })}
              </div>
            </span>
            <button aria-label="Next Month" className="react-datepicker__navigation react-datepicker__navigation--next" type="button" onClick={increaseMonth}>
              <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">{'>'}</span>
            </button>
          </div>
        );
  };

  const yearFormate = showYear ? 'yyyy' : monthFormate();

  return (
    <div className="valueProp">
      <div className={`input-button-container custom-calender ${disable ? 'pointer-none' : ''}`}>
        {isRange ? (
          <DatePicker
            selected={startDate ? moment(startDate).toDate() : null}
            startDate={startDate ? moment(startDate).toDate() : null}
            endDate={endDate ? moment(endDate).toDate() : null}
            selectsRange
            className={classes}
            disableCalendar={disableCalendar}
            calendarContainer={MyContainer}
            customInput={<ExampleCustomInput style={style} fontSize={fontSize} placeholder={placeholder} disabled={disable} />}
            onCalendarOpen={onCalendarOpen}
            onCalendarClose={onCalendarClose}
            closeOnScroll
            closeOnSelect
            showPreviousMonths
            dateFormat={showTime ? `${dateFormat} h:mm aa` : yearFormate}
            showMonthYearPicker={showMonth}
            showYearPicker={showYear}
            name={name}
            popperPlacement={popperPlace}
            popperProps={popperProps}
            showWeekNumbers
            weekLabel=" "
            increaseYear
            decreaseYear
            onChange={showYear ? yearSelection : monthandDateChange()}
            shouldCloseOnSelect={!showMonth || !showYear || !isRange || (startDate && endDate)} // Close only when valid
            showThreeColumnMonthYearPicker
            portalId={portalId}
            onClickOutside={(event) => {
              if (isRange && (!startDate || !endDate)) {
                if (startDate) {
                  onChange([startDate, startDate]);
                }
                if (endDate) {
                  onChange([endDate, endDate]);
                }
                if (!startDate && !endDate) {
                  onChange(Date.now(), Date.now());
                }
                event.stopPropagation();
              }
            }}
            renderCustomHeader={showYear ? undefined : customHeaders()}
            key={keyProp}
            decreaseMonth
            increaseMonth
            forceShowMonthNavigation
            todayButton="Today"
            openToDate={new Date()}
            placeholderText={placeholder}
            disabled={disable}
            readOnly={readOnly}
            innerRef={innerRef}
            filterDate={filterDate}
            yearItemNumber={24}
            timeInputLabel="Add Time: "
            showTimeInput={showDateTime}
            minDate={minDate}
            isClearable={isDateClearable}
          />
        ) : (
          <DatePicker
            dateFormat={showTime ? `${dateFormat} h:mm aa` : yearFormate}
            showMonthYearPicker={showMonth}
            showYearPicker={showYear}
            name={name}
            popperPlacement={popperPlace}
            popperProps={popperProps}
            showWeekNumbers
            weekLabel=" "
            increaseYear
            decreaseYear
            shouldCloseOnSelect={!showMonth && !showYear}
            showThreeColumnMonthYearPicker
            portalId={portalId}
            onClickOutside={handleOustSide}
            renderCustomHeader={showYear ? undefined : customHeaders()}
            key={keyProp}
            decreaseMonth
            increaseMonth
            closeOnScroll
            forceShowMonthNavigation
            todayButton="Today"
            openToDate={new Date()}
            placeholderText={placeholder}
            customInput={<ExampleCustomInput style={style} fontSize={fontSize} />}
            disabled={disable}
            calendarContainer={MyContainer}
            selected={valueProp && valueProp !== '' ? new Date(valueProp) : null}
            readOnly={readOnly}
            onChange={showYear ? yearSelection : monthandDateChange()}
            innerRef={innerRef}
            filterDate={filterDate}
            className={classes}
            disableCalendar={disableCalendar}
            onCalendarOpen={onCalendarOpen}
            onCalendarClose={onCalendarClose}
            yearItemNumber={24}
            timeInputLabel="Add Time: "
            showTimeInput={showDateTime}
            minDate={minDate}
            isClearable={isDateClearable}
          />
        )}
      </div>
    </div>
  );
}

export default CustomDatePicker;
